/**
 * attackStageLight — the ATT&CK for ICS stage of each Event Trace Light step (R-stage, Foster Rd demo cleanup).
 *
 * The approved mapping (Brian, 2026-11-04, "Apply the stage mapping"):
 *   - the edge agent's own traffic is excluded from the attack chain (no stage);
 *   - scans / port sweeps → Discovery;
 *   - protocol reads → Collection;
 *   - writes → Impair Process Control;
 *   - restart / firmware / mode change → Inhibit Response Function;
 *   - Lateral Movement ONLY for a pivot (a host that was a destination earlier becomes the source of a later
 *     step) or a remote-service protocol (SSH, RDP, SMB, WinRM, …) between two inventoried hosts;
 *   - anything unmatched → "Observed".
 * Command & Control (a public source over IP) is kept as the page had it; the mapping does not address it.
 *
 * Each EVENT is classified, and a step (a cluster of same-source events within 10 s) carries EVERY stage its
 * events map to (Observed only when none of them maps to a stage) — no precedence collapse (coordinator's ruling on PR #248): a recon burst of SYNs and reads is
 * Discovery and Collection; a pivot step that reads, writes and restarts is Lateral, Impair and Inhibit.
 * The previous classifier joined every policy name in a cluster into one stage, so a read step read "Impair"
 * when its cluster also held a write, and it returned Discovery for anything it did not recognise.
 *
 * The agent's traffic: its own events (isEdgeAgentTraffic — flagged by the agent, or from an address in
 * EDGE_AGENT_ADDRESSES, both in utils/briefingPrompt.js) and a device's reply to the agent's address. Excluded
 * steps get the stage null; the Operator Summary states how many there were, so exclusion is never silent.
 *
 * Pure: depends only on its arguments and the imported tables.
 */
import { KNOWN_DEVICES } from '../../constants/topologyLight';
import { edgeAgentAddresses, isEdgeAgentTraffic } from '../../utils/briefingPrompt';

export const OBSERVED = 'Observed';
export const DISCOVERY = 'Discovery';
export const COLLECTION = 'Collection';
export const LATERAL = 'Lateral Movement';
export const C2 = 'Command & Control';
export const IMPAIR = 'Impair Process Control';
export const INHIBIT = 'Inhibit Response Function';

/** Display order of the summary's stage lines (the kill chain's order, then Observed). */
export const STAGE_ORDER = [DISCOVERY, COLLECTION, LATERAL, C2, IMPAIR, INHIBIT, OBSERVED];

/** A source sending bare-SYN probes to at least this many distinct (destination, port) targets is sweeping. */
export const SWEEP_MIN_TARGETS = 3;

// Remote-service protocols (ATT&CK for ICS T0886 Remote Services), by name and by well-known port.
const REMOTE_SERVICE_NAMES = /\b(ssh|rdp|smb|winrm|telnet|vnc)\b/i;
const REMOTE_SERVICE_PORTS = new Set([22, 23, 139, 445, 3389, 5900, 5985, 5986]);

const INHIBIT_FUNCTIONS = new Set(['cold_restart', 'warm_restart', 'stop_application', 'start_application',
  'initialize_application', 'initialize_data']);
const WRITE_FUNCTIONS = new Set(['write', 'select', 'operate', 'direct_operate', 'direct_operate_nr']);
const MODBUS_WRITE_CODES = new Set([5, 6, 15, 16, 22, 23]);
const MODBUS_READ_CODES = new Set([1, 2, 3, 4]);

const INHIBIT_TEXT = /restart|reboot|firmware|mode[ _-]?change/i;
const WRITE_TEXT = /\bwrites?\b|operate|\bcrob\b|setpoint|snmp[ _]set|write_?property/i;
const READ_TEXT = /\breads?\b|\bget\b|read_?property/i;
const SCAN_TEXT = /scan|enumerat|sweep|discovery|who_?is|broadcast|first_contact/i;

const isRfc1918 = (ip) => /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(ip);

const destOf = (e) => e.dest_ip || e.destination_ip || null;
const triggerOf = (e) => (e.details && e.details.triggering_event) || {};
const textOf = (e) => [e.policy_name, e.rule_name, e.details && e.details.policy_name, e.details && e.details.rule_name]
  .filter(Boolean).join(' ');
const protoOf = (e) => String(e.protocol || triggerOf(e).protocol || '').toLowerCase();
const isBareSyn = (e) => triggerOf(e).tcp_flags === 'S';
// TCP's responses: a SYN-ACK or a reset answers a connection the other side opened.
const TCP_RESPONSE_FLAGS = new Set(['SA', 'R', 'RA']);
// Policy and rule names decide an operation only when the decoded packet does not name one: a policy's name is
// about what it watches for, the packet's function is what happened.
const opText = (e) => {
  const te = triggerOf(e);
  return te.dnp3_function_name || te.mms_service || te.modbus_operation ? '' : textOf(e);
};

function inventoried(ip, assetsByIp) {
  return Boolean(ip && ((assetsByIp && assetsByIp[ip]) || KNOWN_DEVICES[ip]));
}

/** The site whose agent addresses apply: the caller's, else the one the events name. */
function siteOf(steps) {
  for (const s of steps) {
    for (const e of s.events || []) {
      const site = e.edge_node_id || (e.details && e.details.edge_node_id);
      if (site) return site;
    }
  }
  return null;
}

function isInhibit(e) {
  const te = triggerOf(e);
  return INHIBIT_FUNCTIONS.has(te.dnp3_function_name) || INHIBIT_TEXT.test(opText(e));
}

function isWrite(e, proto) {
  const te = triggerOf(e);
  const d = e.details || {};
  if (te.dnp3_is_write || te.mms_is_write || te.bacnet_is_write || d.dnp3_is_dangerous_write
    || d.mms_is_write || d.bacnet_is_write) return true;
  if (WRITE_FUNCTIONS.has(te.dnp3_function_name) || te.mms_service === 'write') return true;
  if (/^WRITE/i.test(te.modbus_operation || '')) return true;
  if (proto === 'modbus' && MODBUS_WRITE_CODES.has(Number(te.function_code))) return true;
  return WRITE_TEXT.test(opText(e));
}

function isRead(e, proto) {
  const te = triggerOf(e);
  if (te.dnp3_function_name === 'read' || te.mms_service === 'read') return true;
  if (/^READ/i.test(te.modbus_operation || '') || /\/READ\b/i.test(te.service_hint || '')) return true;
  if (proto === 'modbus' && MODBUS_READ_CODES.has(Number(te.function_code))) return true;
  return READ_TEXT.test(opText(e));
}

function isRemoteService(e, proto) {
  const te = triggerOf(e);
  return REMOTE_SERVICE_NAMES.test(proto) || REMOTE_SERVICE_NAMES.test(te.service_hint || '')
    || REMOTE_SERVICE_PORTS.has(Number(te.dest_port));
}

/** True when the event answers the other side: a TCP response, or the reverse pair was seen earlier. */
function isReply(e, seenPairs) {
  return TCP_RESPONSE_FLAGS.has(triggerOf(e).tcp_flags) || seenPairs.has(`${destOf(e)}>${e.source_ip}`);
}

/** The operation an event performs, as a stage, or null: the first rule that matches, in this order. */
function operationStage(e, proto, src, ctx) {
  if (isInhibit(e)) return INHIBIT;
  if (isWrite(e, proto)) return IMPAIR;
  if (src && !isRfc1918(src) && !inventoried(src, ctx.assetsByIp)
    && ['tcp', 'http', 'https'].includes(proto)) return C2;
  if (proto === 'arp' || proto === 'icmp' || SCAN_TEXT.test(textOf(e))
    || (isBareSyn(e) && ctx.sweepers.has(src))) return DISCOVERY;
  if (isRead(e, proto)) return COLLECTION;
  return null;
}

/**
 * The stages of one event: its operation's stage, plus Lateral Movement when it is a pivot or a remote-service
 * contact between inventoried hosts — a pivot that writes is both Lateral and Impair. A scan probe is never
 * Lateral (a sweep touching port 22 is Discovery). An event matching nothing is Observed.
 * ctx: { assetsByIp, sweepers (sources sweeping), contacted (hosts contacted in earlier steps), seenPairs
 * (src>dst pairs of earlier steps) }.
 */
function eventStages(e, ctx) {
  const proto = protoOf(e);
  const src = e.source_ip || null;
  const dst = destOf(e);
  const op = operationStage(e, proto, src, ctx);
  // A pivot: the source was contacted earlier and now opens contact itself. A reply is not a pivot — that
  // covers answering back to whoever contacted it, since that host's earlier contact is the reverse pair.
  const pivot = Boolean(src && dst && ctx.contacted.has(src) && !isReply(e, ctx.seenPairs));
  const remote = isRemoteService(e, proto) && inventoried(src, ctx.assetsByIp) && inventoried(dst, ctx.assetsByIp);
  const out = op ? [op] : [];
  if (op !== DISCOVERY && (pivot || remote)) out.push(LATERAL);
  return out.length > 0 ? out : [OBSERVED];
}

/**
 * The stages of every step, in order: an array the same length as `steps`. Each entry is null for a step that is
 * the edge agent's own traffic (excluded from the attack chain), else an object { [stage]: events } holding every
 * stage the step's events map to, keyed in STAGE_ORDER, each with the events that produced it.
 * `steps` are buildAttackSteps' steps in time order; options: { siteId, assetsByIp }.
 */
export function attackStages(steps, { siteId, assetsByIp } = {}) {
  const list = steps || [];
  const allEvents = list.flatMap(s => s.events || []);
  const agentAddrs = edgeAgentAddresses(siteId || siteOf(list), allEvents);
  const agentRelated = (e) => isEdgeAgentTraffic(e, agentAddrs) || agentAddrs.has(destOf(e));

  // Sweepers: sources with bare-SYN probes to SWEEP_MIN_TARGETS or more distinct (destination, port) targets.
  const probeTargets = new Map();
  allEvents.forEach(e => {
    if (!e.source_ip || !isBareSyn(e) || agentRelated(e)) return;
    const set = probeTargets.get(e.source_ip) || new Set();
    set.add(`${destOf(e)}:${triggerOf(e).dest_port}`);
    probeTargets.set(e.source_ip, set);
  });
  const sweepers = new Set([...probeTargets].filter(([, t]) => t.size >= SWEEP_MIN_TARGETS).map(([ip]) => ip));

  // contacted: hosts contacted in earlier steps. A reply (isReply) is not a contact — so a scanner receiving
  // SYN-ACKs from the hosts it probed is never mistaken for a pivot — and neither is a scan probe (a Discovery
  // event): being probed does not put an attacker on a host.
  const contacted = new Set();
  const seenPairs = new Set();
  return list.map(step => {
    const events = (step.events || []).filter(e => !agentRelated(e));
    if (events.length === 0) return null;
    const ctx = { assetsByIp, sweepers, contacted, seenPairs };
    const perEvent = events.map(e => eventStages(e, ctx));
    const byStage = {};
    STAGE_ORDER.forEach(st => {
      const produced = events.filter((e, k) => perEvent[k].includes(st));
      if (produced.length > 0) byStage[st] = produced;
    });
    // Observed is the step's fallback, not a stage beside others: a step with any kill-chain stage is not also
    // counted as Observed for its unmatched events (e.g. the FIN/ACKs inside a sweep).
    if (Object.keys(byStage).length > 1) delete byStage[OBSERVED];
    // Only after the step is classified: a step cannot pivot on its own destinations.
    events.forEach((e, k) => {
      const dst = destOf(e);
      if (!dst || !e.source_ip) return;
      if (!perEvent[k].includes(DISCOVERY) && !isReply(e, seenPairs)) contacted.add(dst);
      seenPairs.add(`${e.source_ip}>${dst}`);
    });
    return byStage;
  });
}
